/**
 * Searchable indicator catalogue.
 *
 * Every entry shows what the indicator is and, where it matters, what it does
 * not do — Ichimoku's cloud is not displaced here, VWAP is meaningless on daily
 * bars. Those caveats belong at the moment of adding, not in a tooltip nobody
 * opens.
 */
import type { IndicatorDef } from "@tt/shared";
import { useEffect, useMemo, useRef, useState } from "react";

export function IndicatorPicker({
  catalogue, applied, onAdd, onClose,
}: {
  catalogue: IndicatorDef[];
  applied: Record<string, number>;
  onAdd: (id: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const categories = useMemo(
    () => [...new Set(catalogue.map((d) => d.category))],
    [catalogue],
  );

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return catalogue.filter((d) => {
      if (category && d.category !== category) return false;
      if (!needle) return true;
      return (
        d.name.toLowerCase().includes(needle) ||
        d.short.toLowerCase().includes(needle) ||
        d.id.includes(needle) ||
        d.category.toLowerCase().includes(needle)
      );
    });
  }, [catalogue, q, category]);

  useEffect(() => setCursor(0), [q, category]);

  const grouped = useMemo(() => {
    const map = new Map<string, IndicatorDef[]>();
    for (const d of results) {
      const list = map.get(d.category) ?? [];
      list.push(d);
      map.set(d.category, list);
    }
    return [...map.entries()];
  }, [results]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <input
            ref={inputRef}
            className="input grow"
            placeholder={`Search ${catalogue.length} indicators…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              else if (e.key === "ArrowDown") {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              } else if (e.key === "Enter") {
                const hit = results[cursor];
                if (hit) onAdd(hit.id);
              }
            }}
          />
          <button className="btn" onClick={onClose}>Done</button>
        </div>

        <div className="row" style={{ padding: "0 12px 8px", gap: 4 }}>
          <button
            className={`btn small ${category === null ? "active" : ""}`}
            onClick={() => setCategory(null)}
          >
            all
          </button>
          {categories.map((c) => (
            <button
              key={c}
              className={`btn small ${category === c ? "active" : ""}`}
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="modal-body">
          {results.length === 0 && (
            <div className="empty">Nothing matches “{q}”.</div>
          )}
          {grouped.map(([cat, items]) => (
            <div key={cat}>
              <div className="modal-group">{cat}</div>
              {items.map((d) => {
                const index = results.indexOf(d);
                const count = applied[d.id] ?? 0;
                return (
                  <div
                    key={d.id}
                    className={`ind-row ${index === cursor ? "cursor" : ""}`}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => onAdd(d.id)}
                  >
                    <div className="grow">
                      <div className="row" style={{ gap: 6 }}>
                        <strong>{d.name}</strong>
                        <span className="badge muted">{d.short}</span>
                        {d.pane === "overlay" && <span className="badge">on price</span>}
                        {count > 0 && (
                          <span className="badge good">{count} applied</span>
                        )}
                      </div>
                      <div className="faint small">{d.description}</div>
                      {d.notes.map((n) => (
                        <div className="small" style={{ color: "var(--tone-warn)" }} key={n}>
                          {n}
                        </div>
                      ))}
                    </div>
                    <button className="btn small primary">add</button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
